/**
 * @file shop-font-usage.ts
 * @description «Где используется» свой шрифт магазина (phase19.16 Р1-7; SB99 —
 * шрифт опубликованного вида сайта или сохранённого дизайна удалить нельзя):
 * пункты списка, что сделать, строка о черновике. Можно ли удалить — решает
 * сервер (`canDelete`, 409 font_in_use). Названия ролей в именительном и
 * родительном падеже — из реестра `FONT_ROLES` (@chaiberi/shared; SB10,
 * 2026-10-11: «Рукописный акцент» → «Акцент»).
 */

import { FONT_ROLE_KEYS, FONT_ROLES } from "@chaiberi/shared";

import type { ShopFontUsage } from "./shop-fonts";

const ROLE_NOMINATIVE: Readonly<Record<string, string>> = Object.fromEntries(
  FONT_ROLE_KEYS.map((role) => [role, FONT_ROLES[role].title.toLowerCase()]),
);
const ROLE_GENITIVE: Readonly<Record<string, string>> = Object.fromEntries(
  FONT_ROLE_KEYS.map((role) => [role, FONT_ROLES[role].genitive]),
);

const roleList = (roles: readonly string[], map: Readonly<Record<string, string>>): string =>
  roles.map((r) => map[r.toLowerCase()] ?? r).join(", ");

/** Пункты «используется»: «Опубликованный вид сайта — заголовки», «Дизайн «Как было»». */
export function fontUsageLines(usage: ShopFontUsage): string[] {
  const lines: string[] = [];
  if (usage.publishedRoles.length > 0) lines.push(`Опубликованный вид сайта — ${roleList(usage.publishedRoles, ROLE_NOMINATIVE)}`);
  for (const design of usage.designs) lines.push(`Дизайн «${design.name}»`);
  return lines;
}

/** «Чтобы удалить: …» — что сделать по каждому месту. */
export function fontUnblockText(usage: ShopFontUsage): string {
  const steps: string[] = [];
  if (usage.publishedRoles.length > 0) {
    steps.push(`выберите для ${roleList(usage.publishedRoles, ROLE_GENITIVE)} другой шрифт и опубликуйте вид сайта`);
  }
  if (usage.designs.length === 1) {
    steps.push(`удалите дизайн «${usage.designs[0]!.name}» или обновите его из текущего вида с другим шрифтом`);
  } else if (usage.designs.length > 1) {
    steps.push(`удалите дизайны ${usage.designs.map((d) => `«${d.name}»`).join(", ")} или обновите их из текущего вида с другим шрифтом`);
  }
  return steps.length > 0 ? `Чтобы удалить: ${steps.join("; ")}.` : "";
}

/** Роли текущей темы, в которых стоит шрифт (`theme.fonts.* === key`). */
export function localDraftRoles(fonts: Readonly<Record<string, string | null>>, key: string): string[] {
  return FONT_ROLE_KEYS.filter((role) => fonts[role] === key);
}

/** Шрифт стоял в старых версиях сайта (`olderRevisions` ответа удаления). */
export const OLDER_REVISIONS_TEXT = "В старых версиях сайта этот шрифт заменится на шрифт по умолчанию";

/** Строка о черновике: «Он выбран в черновике для заголовков — …». */
export function draftRolesText(roles: readonly string[]): string | null {
  if (roles.length === 0) return null;
  return `Он выбран в черновике для ${roleList(roles, ROLE_GENITIVE)} — там будет шрифт по умолчанию, пока вы не выберете другой.`;
}
